// Focus by element or id (roving tab index in grids, charts, lists; focus after a removal).

import { publish } from "../shared/publish";

function BlazorFocusElement(element: Element | null): void {
    if (element instanceof HTMLElement) {
        element.focus({ preventScroll: true });
    }
}

// Moves DOM focus to an element by id (used by DwsDataGrid's roving-tabindex keyboard navigation,
// where the target cell is only known by an id computed in C#, not an ElementReference).
// Focuses the element with this id without scrolling; false when it is not on the page (or cannot take focus).
function DwsFocusElementIfPresent(elementId: string): boolean {
    let element = document.getElementById(elementId);

    if (!element || typeof element.focus !== 'function') {
        return false;
    }

    element.focus({ preventScroll: true });
    return document.activeElement === element;
}

// Focuses the first of these ids that is on the page and enabled (a list's next row, then a fallback button), for
// components whose focused control has just been removed. Leaves focus alone when the user has already moved it to
// another control in the meantime (anything but <body>).
function DwsFocusFirstPresent(ids: string[]): boolean {
    if (!ids || (document.activeElement && document.activeElement !== document.body)) {
        return false;
    }

    for (let i = 0; i < ids.length; i++) {
        const element = document.getElementById(ids[i]) as HTMLButtonElement | null;

        if (element && !element.disabled && typeof element.focus === 'function') {
            element.focus({ preventScroll: true });

            if (document.activeElement === element) {
                return true;
            }
        }
    }

    return false;
}

function FocusElementById(elementId: string): void {
    let element = document.getElementById(elementId);

    if (element && typeof element.focus === 'function') {
        element.focus({ preventScroll: false });
    }
}

// Like FocusElementById, for an element in a popup that is still being placed (a DwsPopover stays hidden until it
// is positioned, and a hidden element cannot take focus): tries again on the next frames for up to half a second.
function FocusElementByIdWhenVisible(elementId: string): void {
    let deadline = Date.now() + 500;

    let attempt = function () {
        let element = document.getElementById(elementId);

        if (element && typeof element.focus === 'function') {
            element.focus({ preventScroll: true });

            if (document.activeElement === element) {
                return;
            }
        }

        if (Date.now() < deadline) {
            window.requestAnimationFrame(attempt);
        }
    };

    attempt();
}

// Puts focus back on an element (a DataGrid cell after row activation) only when it was lost to the page body - a
// re-render that re-created the element - and not when something else took it on purpose.
function DwsRefocusIfLost(elementId: string) {
    let active = document.activeElement;

    if (active && active !== document.body && active.isConnected) {
        return;
    }

    let element = document.getElementById(elementId);

    if (element) {
        element.focus({ preventScroll: true });
    }
}

publish("BlazorFocusElement", BlazorFocusElement);
publish("DwsFocusElementIfPresent", DwsFocusElementIfPresent);
publish("DwsFocusFirstPresent", DwsFocusFirstPresent);
publish("FocusElementById", FocusElementById);
publish("FocusElementByIdWhenVisible", FocusElementByIdWhenVisible);
publish("DwsRefocusIfLost", DwsRefocusIfLost);
